import { useCallback, useEffect, useState } from "react";
import { errorMessage } from "../errors";

export interface LoadedData<T> {
  /** The last data loaded; kept while reloading and after a failed reload. */
  data: T | undefined;
  loading: boolean;
  loadError: string | null;
  reload: () => void;
}

/**
 * Loads data when the page opens and every time `load` changes. A null `load` means there is nothing
 * to load yet. `failure` is the message shown when the error has no more specific explanation.
 */
export function useLoadedData<T>(load: (() => Promise<T>) | null, failure: string): LoadedData<T> {
  const [data, setData] = useState<T>();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = useCallback(() => {
    if (!load) return;
    setLoading(true);
    setLoadError(null);
    load()
      .then((loaded) => setData(() => loaded))
      .catch((err: unknown) => setLoadError(errorMessage(err, failure)))
      .finally(() => setLoading(false));
  }, [load, failure]);

  useEffect(reload, [reload]);

  return { data, loading, loadError, reload };
}
